import { Text, TextAreaInput } from 'common-components'
import { Field as FieldStyled } from '../../styled'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useWebWoodContext } from 'context/WebWoodContext'
import { ITextData } from 'types/media'
import debounce from 'lodash.debounce'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { editorSelectors } from 'store/features/editor/selectors'
import { useResponsive } from 'hooks/useResponsive'
import { MOBILE_SIZE_BREAKPOINT } from 'consts'
import { PulseWrapper } from './styled'

const TEXT_AREA_MIN_HEIGHT = 140
const TEXT_AREA_MAX_HEIGHT = 140

const toSingleLineText = (value: string) => value.replace(/\r\n|\r|\n/g, ' ')

interface ITextInputProps {
  text: string
  persistTextData: (
    patch: Partial<ITextData>,
    sizeOverrides?: { width: number; height: number }
  ) => void
  mediaItemId: string
  onDelete: () => void
  id: string
}

export const TextInput = (props: ITextInputProps) => {
  const { text, persistTextData, mediaItemId, onDelete } = props
  const { updateObjectById, getObjectById } = useWebWoodContext()

  // TODO: remove when canvas text editing is enabled
  const isMobile = useResponsive(MOBILE_SIZE_BREAKPOINT)
  const pulseToken = useSelector(editorSelectors.textSidebarPulseToken)
  const isPulseEnabled = !isMobile

  const [localText, setLocalText] = useState(() => toSingleLineText(text))

  const debouncedPersist = useMemo(
    () =>
      debounce((value: string) => {
        const object = getObjectById(mediaItemId)
        if (!object) return
        const sizeOverrides = { width: object?.width, height: object?.height }
        persistTextData({ text: value }, sizeOverrides)
      }, 300),
    [persistTextData, mediaItemId, getObjectById]
  )

  useEffect(() => {
    return () => debouncedPersist.cancel()
  }, [debouncedPersist])

  useEffect(() => {
    setLocalText(toSingleLineText(text))
  }, [text])

  const handleChange = useCallback(
    (value: string) => {
      const singleLine = toSingleLineText(value)

      setLocalText(singleLine)
      updateObjectById(mediaItemId, {
        metadata: { text: singleLine }
      } as Record<string, unknown>)
      if (singleLine.trim()) {
        debouncedPersist(singleLine)
      } else {
        debouncedPersist.cancel()
      }
    },
    [updateObjectById, mediaItemId, debouncedPersist]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
      }
    },
    []
  )

  const handleBlur = useCallback(() => {
    if (!localText.trim()) {
      debouncedPersist.cancel()
      onDelete()
    }
  }, [localText, debouncedPersist, onDelete])

  const textArea = (
    <TextAreaInput
      dataTestId="text-overlay-text-area-input"
      value={localText}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      minHeight={TEXT_AREA_MIN_HEIGHT}
      maxHeight={TEXT_AREA_MAX_HEIGHT}
      isHandleOnPaste={false}
    />
  )

  return (
    <FieldStyled>
      <Text type="subtitle">{translate(T.TEXT_BODY_CONTENT_LABEL)}</Text>
      {/* TODO: remove PulseWrapper when canvas text editing is enabled */}
      {isPulseEnabled ? (
        <PulseWrapper key={pulseToken}>{textArea}</PulseWrapper>
      ) : (
        textArea
      )}
    </FieldStyled>
  )
}
